import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useBookingDraft } from '../../contexts/BookingDraftContext';
import {
  getWorkloadLabelAr,
  getDeliveredPhotosLabelAr,
  getShootingMethodLabelAr,
  getProductPhysicalSizeLabelAr,
} from '../../utils/bookingLabels';
import { bookingService } from '../../services/bookingService';
import { pricingService } from '../../services/pricingService';
import { discountCodeService } from '../../services/discountCodeService';
import { photographerService } from '../../services/photographerService';
import { supabase } from '../../lib/supabase';
import { sendOTP as sendOTPApi, normalizePhone } from '../../sendOtp';
import { verifyOtpAndSignIn } from '../../services/verifyOtpAndSignIn';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { normalizeOtpValue } from '../../components/OtpInput';
import Image from '../../components/AppImage';
import { formatNumber } from '../../utils/formatNumber';
import Toast from '../../components/ui/Toast';
import BackButton from '../../components/BackButton';
import { calculateDistance } from '../../services/locationService';
import {
  validateCheckoutIdentityBeforeOtp,
  isValidCheckoutCustomerEmail,
  isValidCheckoutCustomerName,
} from '../../utils/checkoutIdentityValidation';
import { resolveExperienceLevel } from '../../utils/experienceLevel';

// Helper function to format time to Arabic format (ص/م)
const formatTimeToArabic = (time) => {
  if (!time) return 'غير محدد';
  
  if (typeof time === 'string' && (time.includes('ص') || time.includes('م'))) {
    return time;
  }
  
  const timeStr = String(time);
  const [hours, minutes] = timeStr.split(':');
  const hour = parseInt(hours, 10);
  const min = minutes || '00';
  
  if (isNaN(hour)) return time;
  
  if (hour === 0) {
    return `12:${min} ص`;
  } else if (hour < 12) {
    return `${hour}:${min} ص`;
  } else if (hour === 12) {
    return `12:${min} م`;
  } else {
    const displayHour = hour - 12;
    return `${displayHour}:${min} م`;
  }
};

// Helper function to get display labels
const getDisplayLabel = (bookingData, field, value) => {
  const labelField = `${field}Label`;
  if (bookingData[labelField]) {
    return bookingData[labelField];
  }
  
  const maps = {
    city: {
      'riyadh': 'الرياض',
      'jeddah': 'جدة',
      'dammam': 'الدمام',
      'mecca': 'مكة المكرمة',
      'medina': 'المدينة المنورة',
      'khobar': 'الخبر',
      'taif': 'الطائف',
      'tabuk': 'تبوك',
      'abha': 'أبها',
      'buraidah': 'بريدة'
    },
    photographyType: {
      'wedding': 'تصوير حفلات الزفاف',
      'portrait': 'التصوير الشخصي',
      'event': 'تصوير المناسبات',
      'commercial': 'التصوير التجاري',
      'real-estate': 'تصوير العقارات',
      'food': 'تصوير الطعام',
      'fashion': 'تصوير الأزياء',
      'sports': 'التصوير الرياضي',
      'product': 'تصوير المنتجات'
    },
    locationType: {
      'indoor': 'داخلي (استوديو أو مكان مغلق)',
      'outdoor': 'خارجي (في الهواء الطلق)',
      'venue': 'قاعة أو مكان فعاليات',
      'client-location': 'موقع العميل'
    },
    duration: {
      '1': 'ساعة واحدة',
      '2': 'ساعتان',
      '3': '3 ساعات',
      '4': '4 ساعات',
      '6': '6 ساعات',
      '8': '8 ساعات',
      '10': '10 ساعات',
      '12': '12 ساعة أو أكثر'
    },
    experienceLevel: {
      'certified': 'معتمد',
      'distinguished': 'مميز',
      'elite': 'نخبة',
      // Legacy
      'beginner': 'معتمد',
      'intermediate': 'مميز',
      'advanced': 'مميز',
      'expert': 'نخبة'
    }
  };
  
  if (maps[field] && maps[field][value]) {
    return maps[field][value];
  }
  
  return value || '';
};

/** مستوى خبرة المصور في الحجز المباشر — من ملف المصور وليس من draft قديم */
const getDirectPhotographerExperience = (photographer, bookingData) =>
  resolveExperienceLevel(
    photographer?.experienceLevel ||
    photographer?.experience_level ||
    bookingData?.directPhotographerProfile?.experienceLevel ||
    bookingData?.experienceLevel,
    'distinguished'
  );

const DirectCheckout = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const { draft, updateDraft, clearDraft } = useBookingDraft();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [authStep, setAuthStep] = useState('method');
  const [otpSent, setOtpSent] = useState(false);
  const [msegatId, setMsegatId] = useState(null);
  const [otpCode, setOtpCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const verifyOtpInFlightRef = useRef(false);
  const [bookingCreated, setBookingCreated] = useState(false);
  const [createdBookingId, setCreatedBookingId] = useState(null);
  const [calculatedPrice, setCalculatedPrice] = useState(null);
  const [priceLoading, setPriceLoading] = useState(false);
  const [priceLocked, setPriceLocked] = useState(false);
  const [photographer, setPhotographer] = useState(null);
  const [loadingPhotographer, setLoadingPhotographer] = useState(true);
  // SAFE MODE: Track if initial data load from localStorage is complete
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const photographerLoadStartedRef = useRef(false);
  // Discount code
  const [discountCodeInput, setDiscountCodeInput] = useState('');
  const [discountApplying, setDiscountApplying] = useState(false);
  const [discountInfo, setDiscountInfo] = useState(null); // { code, percent, amount, subtotal, vat, total }
  const [discountError, setDiscountError] = useState('');

  // Form data
  const [formData, setFormData] = useState({
    fullName: userProfile?.full_name || '',
    phone: userProfile?.phone || '',
    email: userProfile?.email || '',
    invoiceNote: '',
    allowWhatsApp: true,
  });

  // Load booking data from draft
  const bookingData = draft || {};

  // Load photographer data - prefer stored profile, otherwise fetch
  useEffect(() => {
    if (bookingData.bookingMode !== 'direct') return;

    const loadPhotographer = async () => {
      // First try to use stored photographer profile from draft
      if (bookingData.directPhotographerProfile) {
        try {
          // Fetch full photographer data to ensure we have all fields
          const photographerId = bookingData.directPhotographerProfile.id || 
                                bookingData.photographerId || 
                                bookingData.preselectedPhotographerId;
          if (photographerId) {
            setLoadingPhotographer(true);
            const photographerData = await photographerService.getPhotographer(photographerId);
            setPhotographer(photographerData);
            const resolvedExp = resolveExperienceLevel(
              photographerData.experienceLevel || photographerData.experience_level,
              'distinguished'
            );
            if (
              bookingData.experienceLevel !== resolvedExp.ui ||
              bookingData.experienceLevelLabel !== resolvedExp.labelAr ||
              bookingData.directPhotographerProfile?.experienceLevel !== resolvedExp.ui
            ) {
              updateDraft({
                experienceLevel: resolvedExp.ui,
                experienceLevelLabel: resolvedExp.labelAr,
                directPhotographerProfile: {
                  ...(bookingData.directPhotographerProfile || {}),
                  experienceLevel: resolvedExp.ui,
                },
              });
            }
          } else {
            const resolvedExp = resolveExperienceLevel(
              bookingData.directPhotographerProfile.experienceLevel,
              'distinguished'
            );
            setPhotographer({
              id: bookingData.directPhotographerProfile.id,
              name: bookingData.directPhotographerProfile.name,
              city: bookingData.directPhotographerProfile.city,
              experienceLevel: resolvedExp.ui,
              user: {
                avatar_url: bookingData.directPhotographerProfile.avatar,
                full_name: bookingData.directPhotographerProfile.name
              }
            });
          }
        } catch (error) {
          console.error('Error loading photographer:', error);
          const resolvedExp = resolveExperienceLevel(
            bookingData.directPhotographerProfile.experienceLevel,
            'distinguished'
          );
          setPhotographer({
            id: bookingData.directPhotographerProfile.id,
            name: bookingData.directPhotographerProfile.name,
            city: bookingData.directPhotographerProfile.city,
            experienceLevel: resolvedExp.ui,
            user: {
              avatar_url: bookingData.directPhotographerProfile.avatar,
              full_name: bookingData.directPhotographerProfile.name
            }
          });
        } finally {
          setLoadingPhotographer(false);
        }
        return;
      }

      // Fallback: fetch photographer by ID
      const photographerId = bookingData.photographerId || bookingData.preselectedPhotographerId;
      if (!photographerId) {
        setToast({
          type: 'error',
          message: 'لم يتم تحديد المصور. يرجى البدء من جديد.',
        });
        setTimeout(() => navigate('/booking-wizard'), 2000);
        return;
      }

      try {
        setLoadingPhotographer(true);
        const photographerData = await photographerService.getPhotographer(photographerId);
        setPhotographer(photographerData);
      } catch (error) {
        console.error('Error loading photographer:', error);
        setToast({
          type: 'error',
          message: 'فشل تحميل بيانات المصور. يرجى المحاولة مرة أخرى.',
        });
      } finally {
        setLoadingPhotographer(false);
      }
    };

    if (photographerLoadStartedRef.current) return;
    photographerLoadStartedRef.current = true;
    loadPhotographer();
  }, [bookingData.bookingMode, bookingData.directPhotographerProfile?.id, bookingData.photographerId, bookingData.preselectedPhotographerId, navigate]);

  // SAFE MODE: Wait for localStorage to load before checking booking data
  useEffect(() => {
    // Give localStorage time to load (BookingDraftContext loads on mount)
    const timer = setTimeout(() => {
      setInitialLoadComplete(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // SAFE MODE: Don't redirect until initial load is complete
    if (!initialLoadComplete) {
      return;
    }
    
    // If no booking data, redirect to booking wizard
    if (!bookingData || Object.keys(bookingData).length === 0 || bookingData.bookingMode !== 'direct') {
      setToast({
        type: 'error',
        message: 'لا توجد بيانات حجز مباشر. يرجى البدء من جديد.',
      });
      setTimeout(() => navigate('/booking-wizard'), 2000);
      return;
    }

    // If authenticated, preload user data and skip auth
    if (user && userProfile) {
      setFormData({
        fullName: userProfile.full_name || '',
        phone: userProfile.phone || '',
        email: userProfile.email || '',
        invoiceNote: '',
        allowWhatsApp: true,
      });
      setAuthStep('complete');
    } else {
      setAuthStep('method');
    }

    // Calculate and lock price
    calculateAndLockPrice();
  }, [user, userProfile, bookingData, navigate, initialLoadComplete]);

  // Calculate and lock price
  const calculateAndLockPrice = async () => {
    // If price is already locked within this session, don't recalculate
    if (priceLocked && calculatedPrice) {
      return;
    }

    // Always recalculate from booking parameters — never reuse a cached price from the draft.
    if (!bookingData.photographyType || !bookingData.city || !bookingData.duration) {
      console.warn('Missing required data for price calculation');
      return;
    }

    try {
      setPriceLoading(true);

      // Load pricing config
      const pricingConfig = await pricingService.getPricingConfig(true);
      
      // Use photographer's experience level if available
      const photographerExperienceLevel = photographer?.experience_level || photographer?.experienceLevel || null;
      
      // Calculate final price using smart pricing system
      const priceBreakdown = pricingService.calculateFinalPrice(
        bookingData, 
        pricingConfig,
        photographerExperienceLevel
      );

      // Lock the price
      const lockedPrice = {
        basePrice: priceBreakdown.baseHourPrice * priceBreakdown.hours,
        vat: priceBreakdown.vatAmount,
        totalPrice: priceBreakdown.total,
        subtotal: priceBreakdown.subtotal,
        breakdown: priceBreakdown.breakdown,
        multipliers: priceBreakdown.multipliers,
        vatPercent: priceBreakdown.vatPercent
      };

      setCalculatedPrice(lockedPrice);
      setPriceLocked(true);

      // Save locked price to draft with pricing snapshot
      updateDraft({
        ...bookingData,
        price: priceBreakdown.total,
        basePrice: priceBreakdown.baseHourPrice * priceBreakdown.hours,
        vat: priceBreakdown.vatAmount,
        priceLocked: true,
        pricingSnapshot: {
          baseHourPrice: priceBreakdown.baseHourPrice,
          hours: priceBreakdown.hours,
          multipliers: priceBreakdown.multipliers,
          subtotal: priceBreakdown.subtotal,
          vatAmount: priceBreakdown.vatAmount,
          vatPercent: priceBreakdown.vatPercent,
          total: priceBreakdown.total,
          breakdown: priceBreakdown.breakdown,
          workloadBucket: priceBreakdown.workloadBucket || bookingData.workloadBucket || null,
          workloadMultiplier: priceBreakdown.workloadMultiplier || 1.0,
          productPhysicalSizeBucket: priceBreakdown.productPhysicalSizeBucket ?? bookingData.productPhysicalSizeBucket ?? null,
          productPhysicalSizeLabel: bookingData.productPhysicalSizeLabel || null,
        }
      });
    } catch (error) {
      console.error('Failed to calculate price:', error);
      setToast({
        type: 'error',
        message: 'فشل حساب السعر. يرجى المحاولة مرة أخرى.',
      });
      const defaultPrice = {
        basePrice: 200,
        vat: 30,
        totalPrice: 230,
        subtotal: 200,
        vatPercent: 15
      };
      setCalculatedPrice(defaultPrice);
      setPriceLocked(true);
    } finally {
      setPriceLoading(false);
    }
  };

  // ── Discount code ─────────────────────────────────────────────────────────
  const handleApplyDiscountCode = async () => {
    const codeTrim = discountCodeInput.trim();
    if (!codeTrim) {
      setDiscountError('يرجى إدخال كود الخصم');
      return;
    }
    if (!calculatedPrice) {
      setDiscountError('يرجى الانتظار حتى يتم حساب السعر');
      return;
    }
    try {
      setDiscountApplying(true);
      setDiscountError('');
      // الخصم يُحسب دائماً على المبلغ الفرعي قبل الضريبة (subtotal)، ثم تُحسب
      // الضريبة على المبلغ بعد الخصم — يعمل بشكل صحيح سواء كانت الضريبة 0%
      // (الوضع الحالي) أو مفعّلة لاحقاً، دون أي تعديل على pricingService.
      const baseSubtotal = calculatedPrice.subtotal ?? calculatedPrice.basePrice ?? 0;
      const vatPercent = calculatedPrice.vatPercent || 0;
      const result = await discountCodeService.validateCode(codeTrim, baseSubtotal);
      const discountedSubtotal = Math.max(0, baseSubtotal - result.discountAmount);
      const discountedVat = Math.round(discountedSubtotal * vatPercent / 100);
      const discountedTotal = discountedSubtotal + discountedVat;
      setDiscountInfo({
        code: result.code,
        percent: result.discountPercent,
        amount: result.discountAmount,
        subtotal: discountedSubtotal,
        vat: discountedVat,
        total: discountedTotal,
      });
      setToast({ type: 'success', message: `تم تطبيق كود الخصم بنجاح (خصم ${result.discountPercent}%)` });
    } catch (error) {
      setDiscountInfo(null);
      setDiscountError(error.message || 'كود الخصم غير صحيح');
    } finally {
      setDiscountApplying(false);
    }
  };

  const handleRemoveDiscountCode = () => {
    setDiscountInfo(null);
    setDiscountCodeInput('');
    setDiscountError('');
  };

  const handleSendOTP = async () => {
    const idCheck = validateCheckoutIdentityBeforeOtp(formData);
    if (!idCheck.ok) {
      setToast({ type: 'error', message: idCheck.message });
      return;
    }
    const normalized = normalizePhone(formData.phone);
    if (!normalized) {
      setToast({ type: 'error', message: 'يرجى إدخال رقم الجوال صحيح' });
      return;
    }
    try {
      setLoading(true);
      setToast(null);
      const data = await sendOTPApi(normalized);
      if (!data?.success) {
        throw new Error(data?.error || 'فشل إرسال الرمز');
      }
      setMsegatId(data.id);
      setOtpCode('');
      setOtpSent(true);
      setAuthStep('verify');
      setToast({ type: 'success', message: 'تم إرسال رمز التحقق إلى هاتفك' });
    } catch (error) {
      console.error('Error sending OTP:', error);
      setToast({
        type: 'error',
        message: error.message || 'فشل إرسال رمز التحقق. يرجى المحاولة مرة أخرى.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = useCallback(async () => {
    if (!otpCode || otpCode.length < 4) {
      setToast({ type: 'error', message: 'يرجى إدخال رمز التحقق الكامل' });
      return;
    }
    const idCheck = validateCheckoutIdentityBeforeOtp(formData);
    if (!idCheck.ok) {
      setToast({ type: 'error', message: idCheck.message });
      return;
    }
    const normalized = normalizePhone(formData.phone);
    if (!normalized) {
      setToast({ type: 'error', message: 'رقم الجوال غير صحيح' });
      return;
    }
    try {
      setVerifying(true);
      setToast(null);
      const data = await verifyOtpAndSignIn({
        phone: normalized,
        code: otpCode,
        role: 'customer',
        msegatId,
        userData: {
          full_name: formData.fullName.trim(),
          phone: normalized,
          email: formData.email.trim(),
          allow_whatsapp: formData.allowWhatsApp,
        },
      });
      if (data?.error) throw new Error(data.error);

      // بدلاً من اتباع الـ magic link الذي يُعيد التوجيه للصفحة الرئيسية،
      // نستخرج الـ token ونُصادق مباشرة للبقاء في صفحة الحجز
      if (data?.redirectUrl) {
        const url = new URL(data.redirectUrl);
        const tokenHash = url.searchParams.get('token');
        if (!tokenHash) throw new Error('فشل الحصول على رمز الدخول');
        const { error: verifyError } = await supabase.auth.verifyOtp({
          token_hash: tokenHash,
          type: 'email',
        });
        if (verifyError) throw new Error(verifyError.message);
        setAuthStep('complete');
        setToast({ type: 'success', message: 'تم التحقق بنجاح' });
        return;
      }

      if (data?.success) {
        setAuthStep('complete');
        setToast({ type: 'success', message: 'تم التحقق بنجاح' });
        return;
      }
      throw new Error('فشل التحقق');
    } catch (error) {
      console.error('Error verifying OTP:', error);
      setToast({
        type: 'error',
        message: error.message || 'رمز التحقق غير صحيح. يرجى المحاولة مرة أخرى.',
      });
    } finally {
      setVerifying(false);
    }
  }, [otpCode, formData, msegatId]);

  useEffect(() => {
    if (authStep !== 'verify' || otpCode.length !== 4 || verifying || verifyOtpInFlightRef.current) return;
    verifyOtpInFlightRef.current = true;
    handleVerifyOTP().finally(() => {
      verifyOtpInFlightRef.current = false;
    });
  }, [authStep, otpCode, verifying, handleVerifyOTP]);

  // Confirm booking
  const handleConfirmBooking = async () => {
    if (!user) {
      setToast({ type: 'error', message: 'يرجى تسجيل الدخول أولاً' });
      return;
    }

    try {
      setSaving(true);
      setToast(null);

      const currentUser = user;
      const finalPrice = discountInfo?.total ?? (calculatedPrice?.totalPrice || bookingData.price || 0);

      if (!finalPrice || finalPrice === 0) {
        setToast({ type: 'error', message: 'يرجى حساب السعر أولاً' });
        return;
      }

      if (!isValidCheckoutCustomerName(formData.fullName)) {
        setToast({ type: 'error', message: 'يرجى إدخال الاسم الاول والاخير (حرفان على الأقل)' });
        setSaving(false);
        return;
      }
      if (!isValidCheckoutCustomerEmail(formData.email)) {
        setToast({ type: 'error', message: 'يرجى إدخال بريد إلكتروني صالح' });
        setSaving(false);
        return;
      }

      // Always build a fresh pricing snapshot from the current calculatedPrice — never reuse cached/old snapshots
      const resolvedExp = getDirectPhotographerExperience(photographer, bookingData);
      const pricingSnapshot = {
        baseHourPrice: calculatedPrice.basePrice / (calculatedPrice.breakdown?.find(b => b.type === 'hours')?.value || 1),
        hours: calculatedPrice.breakdown?.find(b => b.type === 'hours')?.value || 1,
        multipliers: calculatedPrice.multipliers || {},
        subtotal: discountInfo?.subtotal ?? (calculatedPrice.subtotal || calculatedPrice.basePrice),
        vatAmount: discountInfo?.vat ?? calculatedPrice.vat,
        vatPercent: calculatedPrice.vatPercent || 15,
        total: finalPrice,
        breakdown: calculatedPrice.breakdown || [],
        subtotalBeforeDiscount: discountInfo ? (calculatedPrice.subtotal || calculatedPrice.basePrice) : null,
        discountCode: discountInfo?.code || null,
        discountPercent: discountInfo?.percent || null,
        discountAmount: discountInfo?.amount || null,
        photographyType: bookingData.photographyType,
        city: bookingData.city,
        locationType: bookingData.locationType,
        duration: bookingData.duration,
        experienceLevel: resolvedExp.ui,
        experienceLevelLabel: resolvedExp.labelAr,
        equipment: bookingData.equipment || [],
        shootingMethods: bookingData.shootingMethods || (bookingData.shootingMethod ? [bookingData.shootingMethod] : []),
        shootingMethod: bookingData.shootingMethod || null,
        shootingPlaceType: bookingData.shootingPlaceType || null,
        workloadBucket: bookingData.workloadBucket || null,
        workloadMultiplier: calculatedPrice.multipliers?.workload?.multiplier || 1.0,
        productPhysicalSizeBucket: bookingData.productPhysicalSizeBucket || null,
        productPhysicalSizeLabel: bookingData.productPhysicalSizeLabel || null,
        deliverableMode: bookingData.deliverableMode || 'custom',
        photosCount: bookingData.photosCount ?? 0,
        videosCount: bookingData.videosCount ?? 0,
        shortVideosCount: bookingData.shortVideosCount ?? 0,
        deliverableLabel: bookingData.deliverableLabel || null,
        deliverables: calculatedPrice.deliverables || null,
        calculatedAt: new Date().toISOString(),
        version: '2.1'
      };

      // Get photographer ID for direct booking
      const photographerId = bookingData.photographerId || bookingData.preselectedPhotographerId;
      if (!photographerId) {
        setToast({ type: 'error', message: 'لم يتم تحديد المصور. يرجى المحاولة مرة أخرى.' });
        return;
      }

      // Validate service radius if precise location is available
      if (bookingData.preciseLocation?.lat && bookingData.preciseLocation?.lng && photographer) {
        const serviceCenterLat = photographer.serviceCenterLat || photographer.service_center_lat;
        const serviceCenterLng = photographer.serviceCenterLng || photographer.service_center_lng;
        const serviceRadiusKm = photographer.serviceRadiusKm || photographer.service_radius_km || 20;

        if (serviceCenterLat && serviceCenterLng) {
          const distanceKm = calculateDistance(
            parseFloat(bookingData.preciseLocation.lat),
            parseFloat(bookingData.preciseLocation.lng),
            parseFloat(serviceCenterLat),
            parseFloat(serviceCenterLng)
          );

          console.log('📏 Distance check:', {
            customerLocation: { lat: bookingData.preciseLocation.lat, lng: bookingData.preciseLocation.lng },
            photographerServiceCenter: { lat: serviceCenterLat, lng: serviceCenterLng },
            distanceKm: distanceKm.toFixed(2),
            serviceRadiusKm: serviceRadiusKm,
            withinRadius: distanceKm <= serviceRadiusKm
          });

          if (distanceKm > serviceRadiusKm) {
            setToast({ 
              type: 'error', 
              message: `موقعك يبعد ${distanceKm.toFixed(1)} كم عن نطاق خدمة المصور (${serviceRadiusKm} كم). يرجى اختيار موقع أقرب أو البحث عن مصور آخر.` 
            });
            setSaving(false);
            return;
          }
        }
      }

      // ── فحص تعارض التاريخ عبر RPC (SECURITY DEFINER يتجاوز RLS) ──────────────
      const bookingDateStr = bookingData.date || bookingData.bookingDate;
      if (bookingDateStr && photographerId) {
        const { data: isAvailable } = await supabase
          .rpc('is_photographer_available_on_date', {
            p_photographer_id: photographerId,
            p_booking_date: bookingDateStr,
          });

        if (isAvailable === false) {
          setToast({
            type: 'error',
            message: 'المصور لديه حجز مؤكد في هذا التاريخ. يرجى اختيار تاريخ آخر أو العودة لاختيار مصور مختلف.',
          });
          setSaving(false);
          return;
        }
      }

      // Create booking with preselected photographer
      const bookingPayload = {
        customerId: currentUser.id,
        photographyType: bookingData.photographyType || bookingData.serviceType || 'تصوير',
        city: bookingData.city || 'الرياض',
        locationType: bookingData.locationType || 'موقع عام',
        location: bookingData.location || `${bookingData.locationType || 'موقع'}، ${bookingData.city || 'الرياض'}`,
        locationDetails: formData.invoiceNote || null,
        date: bookingData.date || bookingData.bookingDate,
        bookingDate: bookingData.date || bookingData.bookingDate,
        time: bookingData.time || '10:00',
        bookingTime: bookingData.time || '10:00',
        duration: bookingData.duration || '2 ساعة',
        equipment: bookingData.equipment || [],
        experienceLevel: resolvedExp.ui,
        finalPrice: finalPrice,
        price: finalPrice,
        basePrice: calculatedPrice?.basePrice || finalPrice * 0.85,
        vat: calculatedPrice?.vat || finalPrice * 0.15,
        pricingSnapshot: pricingSnapshot,
        notes: formData.invoiceNote || null,
        shootingPlaceType: bookingData.shootingPlaceType,
        preciseLocation: bookingData.preciseLocation,
        photographerStudioLocationSnapshot: bookingData.photographerStudioLocationSnapshot,
        workloadBucket: bookingData.workloadBucket || null,
        workloadMultiplier: pricingSnapshot.workloadMultiplier || 1.0,
        productPhysicalSizeBucket: bookingData.productPhysicalSizeBucket || null,
        productPhysicalSizeLabel: bookingData.productPhysicalSizeLabel || null,
        deliverableMode: bookingData.deliverableMode || 'custom',
        photosCount: bookingData.photosCount ?? 0,
        videosCount: bookingData.videosCount ?? 0,
        shortVideosCount: bookingData.shortVideosCount ?? 0,
        deliverableLabel: bookingData.deliverableLabel || null,
        // Direct booking: assign photographer directly
        assignedPhotographerId: photographerId,
      };

      const createdBooking = await bookingService.createBooking(bookingPayload);

      // احتساب استخدام كود الخصم فعلياً بعد نجاح إنشاء الحجز فقط (وليس عند الضغط
      // على "تطبيق")، حتى لا تُستهلك حصّة الاستخدام من عملاء لم يكملوا الحجز.
      if (discountInfo) {
        discountCodeService
          .redeemCode(discountInfo.code, createdBooking.id, discountInfo.amount)
          .catch((err) => console.warn('Failed to redeem discount code (non-blocking):', err));
      }

      try {
        const phoneNorm = normalizePhone(formData.phone);
        await supabase
          .from('user_profiles')
          .update({
            full_name: formData.fullName.trim(),
            email: formData.email.trim().toLowerCase(),
            ...(phoneNorm ? { phone: phoneNorm } : {}),
            updated_at: new Date().toISOString(),
          })
          .eq('id', currentUser.id);
      } catch (syncErr) {
        console.warn('Failed to sync user_profiles after direct booking:', syncErr);
      }

      // إشعار التطبيق + واتساب (إن وُجد) يتمان داخل createBooking — لا حاجة لإرسال هنا
      setCreatedBookingId(createdBooking.id);
      setBookingCreated(true);
      clearDraft();

      // GA4: حدث إنشاء حجز مباشر
      if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
        window.gtag('event', 'booking_created', { event_category: 'booking', event_label: 'direct_booking', value: 1 });
      }

      // SAFE MODE: Navigate to booking-waiting page (same flow as normal booking)
      // This allows the photographer to accept/reject via WhatsApp
      navigate(`/booking-waiting/${createdBooking.id}`);
    } catch (error) {
      console.error('Error creating booking:', error);
      setToast({
        type: 'error',
        message: error.message || 'فشل إنشاء الحجز. يرجى المحاولة مرة أخرى.',
      });
    } finally {
      setSaving(false);
    }
  };

  // SAFE MODE: Show loading while waiting for initial data load
  if (!initialLoadComplete || loadingPhotographer) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-slate-50 to-slate-50 flex items-center justify-center" dir="rtl">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-slate-500">
            {!initialLoadComplete ? 'جاري تحميل البيانات...' : 'جاري تحميل بيانات المصور...'}
          </p>
        </div>
      </div>
    );
  }

  if (!photographer) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-slate-50 to-slate-50 flex items-center justify-center" dir="rtl">
        <div className="text-center">
          <Icon name="AlertCircle" size={64} className="mx-auto mb-4 text-error" />
          <p className="text-lg font-semibold text-slate-900 mb-4">لم يتم العثور على المصور</p>
          <Button onClick={() => navigate('/booking-wizard')} iconName="ArrowRight">العودة إلى الحجز</Button>
        </div>
      </div>
    );
  }

  const displayTotal = discountInfo ? discountInfo.total : calculatedPrice?.totalPrice;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-slate-50 to-slate-50" dir="rtl">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 py-8 md:py-12">
        <div className="max-w-4xl mx-auto">
          <div className="mb-4">
            <BackButton to="/booking-wizard" label="العودة إلى خطوات الحجز" />
          </div>
          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2">تأكيد الحجز</h1>
            <p className="text-slate-600">يرجى مراجعة التفاصيل وإكمال المعلومات المطلوبة</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">

              {/* Photographer Info */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
                <h2 className="text-lg font-semibold text-slate-900 mb-4">المصور المحدد</h2>
                <div className="flex items-center gap-4">
                  {(photographer.user?.avatar_url || photographer.user?.avatarUrl) && (
                    <Image
                      src={photographer.user?.avatar_url || photographer.user?.avatarUrl}
                      alt={photographer.user?.full_name || photographer.user?.fullName || photographer.name || 'مصور'}
                      className="w-16 h-16 rounded-full object-cover"
                    />
                  )}
                  <div className="flex-1">
                    <p className="font-semibold text-lg text-slate-900">
                      {photographer.user?.full_name ||
                       photographer.user?.fullName ||
                       photographer.name ||
                       bookingData.directPhotographerProfile?.name ||
                       'مصور'}
                    </p>
                    {(photographer.city || bookingData.directPhotographerProfile?.city) && (
                      <p className="text-slate-500 mb-1">
                        {photographer.city || bookingData.directPhotographerProfile?.city}
                      </p>
                    )}
                    {photographer && (
                      <p className="text-sm text-slate-500">
                        مستوى الخبرة: {getDirectPhotographerExperience(photographer, bookingData).labelAr}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Booking Summary */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
                <h2 className="text-lg font-semibold text-slate-900 mb-4">تفاصيل الحجز</h2>
                <div className="space-y-4">

                  <div className="py-3 border-b border-slate-200">
                    <p className="text-sm text-slate-600 mb-1">نوع الخدمة</p>
                    <p className="font-semibold text-slate-900">
                      {getDisplayLabel(bookingData, 'photographyType', bookingData.photographyType || bookingData.serviceType) || 'تصوير'}
                    </p>
                  </div>

                  {(bookingData.shootingMethod || bookingData.shootingMethodLabels?.length > 0) && (
                    <div className="py-3 border-b border-slate-200">
                      <p className="text-sm text-slate-600 mb-1">وسيلة التصوير</p>
                      <p className="font-semibold text-slate-900">
                        {bookingData.shootingMethodLabels?.length > 0
                          ? bookingData.shootingMethodLabels.join('، ')
                          : getShootingMethodLabelAr(bookingData.shootingMethod)}
                      </p>
                    </div>
                  )}

                  {bookingData.workloadBucket && (
                    <div className="py-3 border-b border-slate-200">
                      <p className="text-sm text-slate-600 mb-1">حجم العمل</p>
                      <p className="font-semibold text-slate-900">
                        {getWorkloadLabelAr(bookingData.photographyType || bookingData.serviceType, bookingData.workloadBucket)}
                      </p>
                    </div>
                  )}

                  {(bookingData.photographyType === 'product' || bookingData.serviceType === 'product') &&
                    bookingData.productPhysicalSizeBucket && (
                      <div className="py-3 border-b border-slate-200">
                        <p className="text-sm text-slate-600 mb-1">حجم المنتجات</p>
                        <p className="font-semibold text-slate-900 text-pretty">
                          {getProductPhysicalSizeLabelAr(bookingData.productPhysicalSizeBucket)}
                        </p>
                      </div>
                    )}

                  {bookingData.deliveredPhotosBucket && (
                    <div className="py-3 border-b border-slate-200">
                      <p className="text-sm text-slate-600 mb-1">الصور المسلّمة</p>
                      <p className="font-semibold text-slate-900">
                        {getDeliveredPhotosLabelAr(bookingData.deliveredPhotosBucket)}
                      </p>
                    </div>
                  )}

                  <div className="py-3 border-b border-slate-200">
                    <p className="text-sm text-slate-600 mb-1">المدينة</p>
                    <p className="font-semibold text-slate-900">
                      {getDisplayLabel(bookingData, 'city', bookingData.city) || 'الرياض'}
                    </p>
                  </div>

                  <div className="py-3 border-b border-slate-200">
                    <p className="text-sm text-slate-600 mb-1">موقع التصوير</p>
                    {bookingData.shootingPlaceType === 'photographer_studio' ? (
                      <div>
                        <p className="font-semibold text-slate-900 mb-1">التصوير في استديو المصور</p>
                        <p className="text-sm text-slate-500">سيتم عرض عنوان الاستديو بعد تأكيد الحجز</p>
                      </div>
                    ) : bookingData.preciseLocation?.address_text ? (
                      <div>
                        <p className="font-semibold text-slate-900">{bookingData.preciseLocation.address_text}</p>
                        {bookingData.preciseLocation.source === 'gps' && <p className="text-xs text-slate-500 mt-1">📍 باستخدام GPS</p>}
                        {bookingData.preciseLocation.source === 'search' && <p className="text-xs text-slate-500 mt-1">🔍 من البحث</p>}
                        {bookingData.preciseLocation.source === 'pin' && <p className="text-xs text-slate-500 mt-1">📍 من الخريطة</p>}
                      </div>
                    ) : bookingData.shootingPlaceType ? (
                      <p className="font-semibold text-slate-900">{getDisplayLabel(bookingData, 'locationType', bookingData.shootingPlaceType)}</p>
                    ) : (
                      <div className="bg-amber-50 rounded-lg p-3">
                        <div className="flex items-center gap-2 text-amber-600">
                          <Icon name="AlertCircle" size={16} />
                          <p className="text-sm font-medium">الموقع الدقيق غير محدد</p>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="py-3 border-b border-slate-200">
                    <p className="text-sm text-slate-600 mb-1">التاريخ والوقت</p>
                    <p className="font-semibold text-slate-900">
                      {bookingData.date
                        ? (() => {
                            const date = new Date(bookingData.date);
                            const weekday = date.toLocaleDateString('ar-SA-u-ca-gregory', { weekday: 'long' });
                            const month = date.toLocaleDateString('ar-SA-u-ca-gregory', { month: 'long' });
                            const day = date.getDate();
                            const year = date.getFullYear();
                            return `${weekday}، ${day} ${month} ${year}`;
                          })()
                        : 'غير محدد'}{' '}
                      {bookingData.time ? `- ${formatTimeToArabic(bookingData.time)}` : ''}
                    </p>
                  </div>

                  <div className="py-3 border-b border-slate-200">
                    <p className="text-sm text-slate-600 mb-1">المدة</p>
                    <p className="font-semibold text-slate-900">
                      {getDisplayLabel(bookingData, 'duration', bookingData.duration) || 'غير محدد'}
                    </p>
                  </div>

                  {/* المخرجات */}
                  {(bookingData.photosCount > 0 || bookingData.videosCount > 0 || bookingData.shortVideosCount > 0) && (
                    <div className="py-3 border-b border-slate-200">
                      <p className="text-sm text-slate-600 mb-1">المخرجات</p>
                      <p className="font-semibold text-slate-900">
                        {bookingData.deliverableLabel || [
                          bookingData.photosCount > 0 && `${bookingData.photosCount} صورة`,
                          bookingData.videosCount > 0 && `${bookingData.videosCount} فيديو طويل`,
                          bookingData.shortVideosCount > 0 && `${bookingData.shortVideosCount} فيديو قصير`,
                        ].filter(Boolean).join(' + ')}
                      </p>
                    </div>
                  )}

                  {/* مستوى التعديل */}
                  {(() => {
                    const EDITING_KEYS = ['basic-editing', 'professional-editing', 'no-editing'];
                    const editingKey = (bookingData.equipment || []).find(eq => EDITING_KEYS.includes(eq));
                    if (!editingKey) return null;
                    const EDITING_LABELS = {
                      'professional-editing': 'تعديل احترافي متقدم',
                      'basic-editing': 'تعديل أساسي (تصحيح إضاءة وألوان)',
                      'no-editing': 'صور أصلية بدون تعديل',
                    };
                    return (
                      <div className="py-3 border-b border-slate-200">
                        <p className="text-sm text-slate-600 mb-1">مستوى التعديل</p>
                        <p className="font-semibold text-slate-900">{EDITING_LABELS[editingKey] || editingKey}</p>
                      </div>
                    );
                  })()}

                  {/* المعدات الإضافية — فقط غير خيارات التعديل */}
                  {(() => {
                    const EDITING_KEYS = ['basic-editing', 'professional-editing', 'no-editing'];
                    const otherEq = (bookingData.equipment || [])
                      .map((eq, idx) => ({ eq, idx }))
                      .filter(({ eq }) => !EDITING_KEYS.includes(eq));
                    if (otherEq.length === 0) return null;
                    const equipmentLabels = {
                      'drone': 'تصوير بالطائرة المسيرة (Drone)',
                      'lighting': 'معدات إضاءة احترافية',
                      'video': 'تصوير فيديو',
                      'backdrop': 'خلفيات وديكورات',
                      'assistant': 'مساعد مصور',
                      'makeup': 'خدمة المكياج',
                    };
                    return (
                      <div className="py-3 border-b border-slate-200">
                        <p className="text-sm text-slate-600 mb-1">الخدمات الإضافية</p>
                        <div className="flex flex-wrap gap-2">
                          {otherEq.map(({ eq, idx }) => {
                            const displayLabel = bookingData.equipmentLabels?.[idx] || equipmentLabels[eq] || eq;
                            return (
                              <span key={idx} className="px-3 py-1 bg-primary/10 text-primary rounded-lg text-sm">
                                {displayLabel}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}

                </div>
              </div>

              {/* ملاحظات للمصور */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
                <h2 className="text-lg font-semibold text-slate-900 mb-1">ملاحظات للمصور</h2>
                <p className="text-sm text-slate-500 mb-4">اختياري — ستُرسل مباشرة للمصور مع تأكيد الحجز</p>
                <textarea
                  value={formData.invoiceNote}
                  onChange={(e) => setFormData((prev) => ({ ...prev, invoiceNote: e.target.value }))}
                  rows={3}
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:bg-white transition-colors"
                  placeholder="مثال:  لديّ طلب خاص..."
                />
              </div>

              {/* Auth Gate */}
              {authStep !== 'complete' && (
                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
                  <h2 className="text-lg font-semibold text-slate-900 mb-4">المصادقة</h2>
                  {authStep === 'method' && (
                    <div className="space-y-4">
                      <p className="text-sm text-slate-600">
                        أدخل بياناتك في الاسفل.
                      </p>
                      <Input
                        label="الاسم الاول والاخير "
                        value={formData.fullName}
                        onChange={(e) => setFormData((prev) => ({ ...prev, fullName: e.target.value }))}
                        required
                      />
                      <Input
                        label="البريد الإلكتروني"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                        required
                        dir="ltr"
                        className="text-left"
                      />
                      <div>
                        <Input
                          label="رقم الجوال"
                          type="tel"
                          inputMode="numeric"
                          value={formData.phone}
                          onChange={(e) => {
                            const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                            setFormData((prev) => ({ ...prev, phone: val }));
                          }}
                          placeholder="05xxxxxxxx"
                          maxLength={10}
                          dir="ltr"
                        />
                        {formData.phone && !normalizePhone(formData.phone) && (
                          <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                            رقم الجوال يجب أن يكون 10 أرقام ويبدأ بـ 05
                          </p>
                        )}
                      </div>
                      <Button
                        variant="default"
                        onClick={handleSendOTP}
                        loading={loading}
                        disabled={
                          !normalizePhone(formData.phone) ||
                          !validateCheckoutIdentityBeforeOtp(formData).ok
                        }
                        className="w-full"
                      >
                        إرسال رمز التحقق
                      </Button>
                    </div>
                  )}
                  {authStep === 'verify' && (
                    <div className="space-y-4">
                      <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-sm text-slate-700 space-y-1">
                        <p>
                          <span className="text-slate-500">الاسم:</span>{' '}
                          <span className="font-medium">{formData.fullName.trim() || '—'}</span>
                        </p>
                        <p dir="ltr" className="text-left">
                          <span className="text-slate-500">البريد:</span>{' '}
                          <span className="font-medium">{formData.email.trim() || '—'}</span>
                        </p>
                        <p dir="ltr" className="text-left">
                          <span className="text-slate-500">الجوال:</span>{' '}
                          <span className="font-semibold">{normalizePhone(formData.phone) || formData.phone}</span>
                        </p>
                      </div>
                      <p className="text-slate-600 text-sm">
                        تم إرسال الرمز إلى {normalizePhone(formData.phone) || formData.phone}
                      </p>
                      <Input
                        label="رمز التحقق"
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        name="one-time-code"
                        value={otpCode}
                        onChange={(e) => setOtpCode(normalizeOtpValue(e.target.value))}
                        placeholder="0000"
                        maxLength={4}
                        dir="ltr"
                        className="text-center text-2xl font-mono"
                      />
                      <Button
                        onClick={handleVerifyOTP}
                        loading={verifying}
                        disabled={otpCode.length < 4}
                        className="w-full"
                      >
                        تأكيد
                      </Button>
                    </div>
                  )}
                </div>
              )}

            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-4">

                {/* Price Summary */}
                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
                  <h2 className="text-lg font-semibold text-slate-900 mb-4">ملخص السعر</h2>
                  {priceLoading ? (
                    <div className="text-center py-6">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                      <p className="text-slate-500 text-sm">جاري حساب السعر...</p>
                    </div>
                  ) : calculatedPrice ? (
                    <div className="space-y-3">
                      <div className="space-y-1.5">
                        <div className="flex justify-between text-sm text-slate-500">
                          <span>رسوم المصور (80%)</span>
                          <span>{formatNumber(Math.round((displayTotal || 0) * 0.80))} ر.س</span>
                        </div>
                        <div className="flex justify-between text-sm text-slate-500">
                          <span>رسوم المنصة (20%)</span>
                          <span>{formatNumber(Math.round((displayTotal || 0) * 0.20))} ر.س</span>
                        </div>
                        {discountInfo && (
                          <div className="flex justify-between text-sm text-success font-medium">
                            <span>خصم ({discountInfo.code} - {discountInfo.percent}%)</span>
                            <span>- {formatNumber(discountInfo.amount)} ر.س</span>
                          </div>
                        )}
                      </div>
                      <div className="border-t-2 border-slate-200 pt-3 flex justify-between font-bold text-slate-900">
                        <span>الإجمالي</span>
                        <span className="text-primary text-lg">{formatNumber(displayTotal)} ر.س</span>
                      </div>
                      <p className="text-xs text-slate-400 text-left mt-1">السعر النهائي شامل جميع الرسوم</p>

                      {/* Discount Code */}
                      <div className="pt-2">
                        {!discountInfo ? (
                          <div>
                            <label className="block text-xs font-medium text-slate-600 mb-2">لديك كود خصم؟</label>
                            <div className="flex gap-2">
                              <input
                                type="text"
                                value={discountCodeInput}
                                onChange={(e) => {
                                  setDiscountCodeInput(e.target.value.toUpperCase());
                                  setDiscountError('');
                                }}
                                placeholder="أدخل الكود"
                                className="flex-1 min-w-0 px-3 py-2 rounded-lg border border-slate-300 text-sm uppercase focus:outline-none focus:ring-2 focus:ring-primary"
                                dir="ltr"
                              />
                              <Button
                                type="button"
                                variant="outline"
                                onClick={handleApplyDiscountCode}
                                loading={discountApplying}
                                disabled={discountApplying || !discountCodeInput.trim()}
                              >
                                تطبيق
                              </Button>
                            </div>
                            {discountError && <p className="text-xs text-red-500 mt-1.5">{discountError}</p>}
                          </div>
                        ) : (
                          <div className="flex items-center justify-between bg-success/10 border border-success/20 rounded-lg px-3 py-2.5">
                            <div className="flex items-center gap-2">
                              <Icon name="Tag" size={16} className="text-success flex-shrink-0" />
                              <div>
                                <p className="text-sm font-semibold text-success" dir="ltr">{discountInfo.code} — خصم {discountInfo.percent}%</p>
                                <p className="text-xs text-slate-500">وفّرت {formatNumber(discountInfo.amount)} ر.س</p>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={handleRemoveDiscountCode}
                              className="text-xs text-slate-400 hover:text-red-500 flex-shrink-0"
                            >
                              إزالة
                            </button>
                          </div>
                        )}
                      </div>

                      {priceLocked && (
                        <div className="flex items-center gap-2 text-success text-xs bg-success/5 rounded-lg px-3 py-2 mt-2">
                          <Icon name="Lock" size={14} />
                          <span>السعر مقفل ومضمون</span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-slate-500 text-sm">لا يمكن حساب السعر حالياً</p>
                  )}
                </div>

                {/* Confirm Button */}
                <Button
                  onClick={handleConfirmBooking}
                  loading={saving}
                  disabled={saving || priceLoading || !calculatedPrice || authStep !== 'complete'}
                  className="w-full"
                  size="lg"
                >
                  {saving ? 'جاري إنشاء الحجز...' : 'تأكيد الحجز'}
                </Button>

              </div>
            </div>
          </div>
        </div>
      </div>

      {toast && (
        <Toast
          type={toast.type}
          message={toast.message}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default DirectCheckout;
